import { jsx as _jsx } from "react/jsx-runtime";
/**
 * A canvas item's own pixels and their light list: the object the per-item cap measures for the
 * item's rect, and the one material state it hands the meshes inside.
 */
import { createContext, useContext, useMemo } from 'react';
import { LIT_ITEM_USER_DATA } from '../lighting2d/itemLightCap';
import { useCanvasItemLighting } from '../lighting2d/useCanvasItemLighting';
import { canvasItemFacing } from '../canvasItemFacing';
import { canvasItemBlendState, } from '../../resources/materials/canvasitemmaterial/renderer';
import { CanvasItemMaterialBlendMode } from '../../godot/scene/canvasItemMaterial';
/** Undefined outside a lit item, where a mesh stays unlit as every 3D consumer needs. */
const CanvasItemPixelMaterialContext = createContext(undefined);
CanvasItemPixelMaterialContext.displayName = 'CanvasItemPixelMaterialContext';
/**
 * The pixel material of the enclosing lit canvas item, for a leaf mesh deep inside a painter that
 * the item's `children` render prop does not reach.
 *
 * @canonical The lit pixel material of the enclosing canvas item
 */
export function useCanvasItemPixelMaterial() {
    return useContext(CanvasItemPixelMaterialContext);
}
/**
 * The `materialProgramInputs` `merge` of a leaf mesh in a canvas item: the facing, then the lit
 * pixel material of the enclosing item. A leaf outside one stays unlit.
 */
export function useCanvasItemMerge() {
    const pixelMaterial = useCanvasItemPixelMaterial();
    return useMemo(() => [canvasItemFacing(), pixelMaterial], [pixelMaterial]);
}
export function LitCanvasItemPixels({ material, lightMask, zFinal, surface = null, children, }) {
    const { props: lighting, geometryRef } = useCanvasItemLighting({
        material,
        lightMask,
        effectiveZ: zFinal,
        surface,
    });
    const blendMode = material?.blendMode ?? CanvasItemMaterialBlendMode.MIX;
    const pixelMaterial = useMemo(() => ({ ...canvasItemFacing(), ...canvasItemBlendState(blendMode), ...lighting }), [blendMode, lighting]);
    // An `object3D`, not a group: three takes a group's `renderOrder` as the draw key.
    return (_jsx("object3D", { ref: geometryRef, userData: LIT_ITEM_USER_DATA, children: _jsx(CanvasItemPixelMaterialContext.Provider, { value: pixelMaterial, children: typeof children === 'function' ? children(pixelMaterial) : children }) }));
}
//# sourceMappingURL=LitCanvasItemPixels.js.map